"use client";
import { useState, useEffect } from "react";
import {
  Building2,
  Microscope,
  Zap,
  Building,
  Smartphone,
  CheckCircle,
  ArrowRight,
  Cloud,
  Database,
  Server,
  TrendingUp,
} from "lucide-react";
import { AnimatedGradientBackground, GradientPresets } from "@/components/Animation/GradientBackground";
import IconBox from "@/components/Icons/iconbox";

interface TechIcon {
  name: string;
  icon: any;
}

interface Sector {
  id: string;
  icon: any;
  title: string;
  tagline: string;
  description: string;
  howWeDeliver: string[];
  techIcons: TechIcon[];
}

const sectors: Sector[] = [
  {
    id: "public-sector",
    icon: Building2,
    title: "Public Sector & Government",
    tagline: "Modernising public services with integrity and intelligence.",
    description:
      "Public organisations face growing demands for transparency, data integration, and citizen experience — often on ageing systems. Luminar helps departments and agencies design digital strategies that are both ambitious and achievable, balancing modernisation with accountability.",
    howWeDeliver: [
      "Simplify legacy workflows through low-code automation and digital service redesign.",
      "Build secure, cloud-based data platforms with appropriate governance.",
      "Embed analytics to measure performance and impact at every stage.",
      "Facilitate cross-department collaboration using shared knowledge and delivery frameworks.",
    ],
    techIcons: [
      { name: "AWS GovCloud", icon: Cloud },
      { name: "Snowflake", icon: Database },
      { name: "ServiceNow", icon: Server },
      { name: "Tableau", icon: TrendingUp },
    ],
  },
  {
    id: "pharma-life-sciences",
    icon: Microscope,
    title: "Pharma & Life Sciences",
    tagline: "Accelerating discovery through data and delivery discipline.",
    description:
      "The life sciences sector is evolving at speed — with R&D, regulatory, and commercial pressures demanding seamless digital infrastructure. Luminar enables biopharma organisations to harness data, AI, and automation to enhance insight generation, compliance, and patient impact.",
    howWeDeliver: [
      "Build unified R&D data environments connecting clinical, regulatory, and commercial datasets.",
      "Deploy AI models for pharmacovigilance, patient safety, and clinical trial optimisation.",
      "Modernise data governance to support regulatory audits and ethical AI adoption.",
      "Deliver scalable analytics environments integrated with lab and field operations.",
    ],
    techIcons: [
      { name: "Databricks", icon: Database },
      { name: "AWS", icon: Cloud },
      { name: "Python ML", icon: TrendingUp },
      { name: "Power BI", icon: TrendingUp },
    ],
  },
  {
    id: "energy-infrastructure",
    icon: Zap,
    title: "Energy & Infrastructure",
    tagline: "Powering sustainable transition through data and automation.",
    description:
      "Energy, utilities, and infrastructure providers are balancing operational resilience with decarbonisation goals. Luminar helps these organisations design intelligent operations — connecting assets, data, and people to optimise performance and sustainability.",
    howWeDeliver: [
      "Build IoT-enabled data pipelines and predictive maintenance models.",
      "Design real-time control dashboards for energy forecasting and asset utilisation.",
      "Develop ESG analytics platforms integrating carbon, compliance, and financial data.",
      "Embed digital twin frameworks for infrastructure monitoring and decision support.",
    ],
    techIcons: [
      { name: "AWS IoT", icon: Cloud },
      { name: "Databricks", icon: Database },
      { name: "Power BI", icon: TrendingUp },
      { name: "Tableau", icon: TrendingUp },
    ],
  },
  {
    id: "financial-services",
    icon: Building,
    title: "Financial Services",
    tagline: "Navigating modernisation in a regulated world.",
    description:
      "Banks, insurers, and asset managers face relentless regulatory pressure, evolving customer expectations, and technology complexity. Luminar designs and delivers large-scale change programmes that strengthen compliance, simplify technology, and enable growth.",
    howWeDeliver: [
      "Lead multi-system integrations for post-merger and regulatory programmes.",
      "Build data models for risk analytics, liquidity, and capital forecasting.",
      "Automate control testing and governance workflows for compliance assurance.",
      "Modernise reporting and performance dashboards across lines of business.",
    ],
    techIcons: [
      { name: "AWS", icon: Cloud },
      { name: "Snowflake", icon: Database },
      { name: "ServiceNow", icon: Server },
      { name: "Power BI", icon: TrendingUp },
    ],
  },
  {
    id: "technology-consumer",
    icon: Smartphone,
    title: "Technology & Consumer",
    tagline: "Growth through connected experience.",
    description:
      "Technology and consumer brands succeed by delivering unified, intelligent customer ecosystems. Luminar helps these organisations bridge marketing, data, and operations — turning fragmented channels into coherent, data-driven journeys.",
    howWeDeliver: [
      "Integrate CRM, commerce, and data platforms to enable 360-degree customer insight.",
      "Deploy AI personalisation models for smarter engagement and retention.",
      "Optimise omnichannel operations with automation and analytics.",
      "Embed agile experimentation frameworks to continually refine experience design.",
    ],
    techIcons: [
      { name: "Salesforce", icon: Database },
      { name: "AWS", icon: Cloud },
      { name: "Databricks", icon: Database },
      { name: "Tableau", icon: TrendingUp },
    ],
  },
];

export default function SectorsPage() {
  const [expandedSector, setExpandedSector] = useState<string | null>(null);

  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (hash) {
      setExpandedSector(hash);
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, []);

  const toggleSector = (id: string) => {
    setExpandedSector(expandedSector === id ? null : id);
  };

  return (
    <main className="min-h-screen text-text-primary">
      <AnimatedGradientBackground {...GradientPresets.sectors} />

      {/* HERO */}
      <section className="relative max-w-6xl mx-auto px-6">
        <div className="border border-standard pt-40 p-8 sm:p-12 transition-all duration-1000 ease-[cubic-bezier(0.19,1,0.22,1)] opacity-100 translate-y-0">
          <h1 className="h1 pt-24 mb-6">
            Where We <span className="text-text-secondary">Work</span>
          </h1>
          <p className="body-lg mb-6">
            As a sector-focused consultancy, Luminar works where precision and trust matter most
            — in industries defined by complexity, regulation, and scale.
          </p>
          <p className="body-md">
            Our consultants bring sector-specific understanding combined with cross-industry
            innovation, allowing proven methods in one field to elevate outcomes in another.
          </p>
        </div>
      </section>

      {/* SECTORS GRID */}
      <section className="max-w-6xl mx-auto px-6 mt-16 sm:mt-24">
        <div className="space-y-6">
          {sectors.map((sector, index) => {
            const isExpanded = expandedSector === sector.id;
            const colorClass =
              index % 2 === 0 ? "text-accentcolourteal" : "text-accentcolourorange";
            const bgColorClass =
              index % 2 === 0 ? "bg-accentcolourteal" : "bg-accentcolourorange";

            return (
              <div key={sector.id} id={sector.id} className="scroll-mt-24">
                <div className="border border-standard backdrop-blur-sm overflow-hidden transition-all hover:shadow-glass">
                  {/* Header */}
                  <button
                    onClick={() => toggleSector(sector.id)}
                    className="w-full flex items-start justify-between p-6 sm:p-8 text-left group transition-all"
                  >
                    <div className="flex items-start gap-4 sm:gap-6 flex-1">
                      <IconBox icon={sector.icon} index={index} size="lg" />
                      <div className="flex-1">
                        <h2 className="h3 mb-2 sm:mb-3">{sector.title}</h2>
                        <p className="body-lg italic">{sector.tagline}</p>
                      </div>
                    </div>
                    <div
                      className={`flex-shrink-0 transition-transform duration-300 ${
                        isExpanded ? "rotate-90" : ""
                      }`}
                    >
                      <ArrowRight className="w-6 h-6 sm:w-8 sm:h-8 text-accentcolourteal" />
                    </div>
                  </button>

                  {/* Expandable Content */}
                  <div
                    className={`overflow-hidden transition-all duration-500 ${
                      isExpanded ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
                    }`}
                  >
                    <div className="px-6 sm:px-8 pb-6 sm:pb-8 sm:ml-20 space-y-6">
                      {/* Description */}
                      <div>
                        <div className={`h-1 w-12 mb-4 sm:mb-6 ${bgColorClass}`} />
                        <p className="body-md leading-relaxed">{sector.description}</p>
                      </div>

                      {/* How We Deliver */}
                      <div>
                        <h3 className={`h3 mb-4 ${colorClass}`}>How We Deliver</h3>
                        <ul className="space-y-3">
                          {sector.howWeDeliver.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <CheckCircle
                                className={`w-5 h-5 flex-shrink-0 mt-0.5 ${colorClass}`}
                              />
                              <span className="body-md">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Technologies */}
                      <div className="pt-4">
                        <h4 className="body-md font-semibold text-text-primary mb-4">
                          Technologies Used
                        </h4>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                          {sector.techIcons.map((tech, idx) => (
                            <div
                              key={idx}
                              className="flex flex-col items-center justify-center p-3 sm:p-4 border border-standard backdrop-blur-sm rounded-lg min-h-[100px] sm:min-h-[120px]"
                            >
                              <IconBox icon={tech.icon} index={index} size="sm" className="mb-2" />
                              <span className="body-md text-center break-words w-full">
                                {tech.name}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CROSS-SECTOR IMPACT */}
      <section className="max-w-6xl mx-auto px-6 mt-16 sm:mt-24 mb-24">
        <div className="max-w-4xl mx-auto">
          <div className="border-l-4 border-accentcolourteal backdrop-blur-sm pl-8 sm:pl-12 pr-6 sm:pr-8 py-8 sm:py-10">
            <h2 className="h2 mb-4 sm:mb-6">Cross-Sector Impact</h2>
            <p className="body-lg leading-relaxed mb-4 sm:mb-6">
              Across every sector, Luminar is recognised for its ability to turn complex
              transformation into disciplined, measurable execution.
            </p>
            <p className="body-md leading-relaxed">
              We apply a consistent approach built on strategic clarity, robust architecture, and
              thoughtful change management. Each engagement is designed to leave clients stronger
              than before, with the tools, knowledge, and confidence to sustain progress long after
              delivery.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}